/**
 * Bureau credentials and Test connection (mission 7 WP07 T039; FR-001 to FR-004,
 * FR-015, FR-018, SC-012; TX.1, TX.2, TX.9, TEN.4; contracts/credentials-api.md;
 * DM-01M3EZ6VTRWJYNTG7F3SSYX61V: no reveal after Save).
 *
 * Reads never carry a secret: a stored password is `password_set`, a stored key
 * is `key_set` plus its fingerprint, and the username comes back only as
 * `username_masked` (owner decision A2). No type here has a `username` or
 * secret member on the read side.
 *
 * `putCredentials` builds its body key by key (C-006). A secret, and the
 * username, are sent only when the reporter typed one: an absent secret keeps
 * the stored one, and an absent username keeps the stored one (A2), because the
 * client never holds the stored value. The wrapper never logs, and the secret
 * travels in the PUT body only, never in a URL, a query key or router state.
 */
import { api, oneOf } from '../api'
import { BUREAUS, type Bureau, type ProgramIdentifier } from './headerParams'

export { BUREAUS, type Bureau }

const NAMES: Record<Bureau, string> = {
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
  innovis: 'Innovis',
}

/** The one display map for a bureau's name. */
export function bureauName(bureau: Bureau): string {
  return NAMES[bureau]
}

/** `transmission.EffectiveStatus`: the kill switch wins, then no row, then the stored status (TX.2, TX.9). */
export type CredentialStatus = 'not_entered' | 'entered_untested' | 'verified' | 'auth_failed' | 'disabled_by_trellis'
export const CREDENTIAL_STATUSES: readonly CredentialStatus[] = [
  'not_entered',
  'entered_untested',
  'verified',
  'auth_failed',
  'disabled_by_trellis',
]

export type SecretKind = 'password' | 'private_key'

/** The stored credential as every read renders it: masked, fingerprint only. */
export interface CredentialView {
  host: string
  port: number
  /** `cipherstash.Mask(username)`; the full username is never returned (A2). */
  username_masked: string
  remote_path: string
  secret_kind: SecretKind
  password_set: boolean
  key_set: boolean
  /** `SHA256:…` for a key; null for a password. */
  key_fingerprint: string | null
  last_tested_at: string | null
  updated_at: string
}

export interface BureauEntry {
  bureau: Bureau
  status: CredentialStatus
  /** The TX.9 kill switch names this bureau; the explanation is the register's `killSwitchExplanation`. */
  disabled_by_trellis: boolean
  credential: CredentialView | null
  /** The header parameters' masked identifier for this bureau (sheet 40). */
  subscriber_code: ProgramIdentifier
}

/** GET /v1/bureaus: four entries, in bureau order. */
export interface BureausResponse {
  bureaus: BureauEntry[]
}

/**
 * What a Save may send. `username`, `password` and `private_key` are sent only
 * when non-empty; exactly one secret is required unless one is stored.
 */
export interface CredentialsBody {
  host: string
  port: number
  username?: string
  remote_path: string
  password?: string
  private_key?: string
}

export type TestResult = 'success' | 'auth_failed' | 'timeout'

/** POST /v1/bureaus/{bureau}/test. `applied` is false when the credential changed during the test, and for a timeout. */
export interface TestConnectionResult {
  bureau: Bureau
  result: TestResult
  applied: boolean
  status: CredentialStatus
  last_tested_at: string | null
}

function bureauSegment(bureau: Bureau): string {
  return oneOf(bureau, BUREAUS, 'bureau')
}

export async function getBureaus(): Promise<BureausResponse> {
  return api<BureausResponse>('/bureaus')
}

/** PUT /v1/bureaus/{bureau}/credentials (RequireOwner; FR-001). Answers the bureau's entry, masked. */
export async function putCredentials(bureau: Bureau, body: CredentialsBody): Promise<BureauEntry> {
  const path = `/bureaus/${bureauSegment(bureau)}/credentials`
  const clean: Record<string, string | number> = { host: body.host, port: body.port }
  if (body.username) clean['username'] = body.username
  clean['remote_path'] = body.remote_path
  if (body.password) clean['password'] = body.password
  if (body.private_key) clean['private_key'] = body.private_key
  return api<BureauEntry>(path, { method: 'PUT', body: JSON.stringify(clean) })
}

/** POST /v1/bureaus/{bureau}/test (RequireOwner; FR-003, SEC.6). No body. */
export async function testConnection(bureau: Bureau): Promise<TestConnectionResult> {
  return api<TestConnectionResult>(`/bureaus/${bureauSegment(bureau)}/test`, { method: 'POST' })
}
